import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { PageLayout } from "../components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Calendar,
  Clock,
  Users,
  Upload,
  Video,
  User,
  FileText,
  Download,
} from "lucide-react";

const API = `${window.location.origin}/api`;

const AdminSessionsPage = () => {
  const [sessions, setSessions] = useState([]);
  const [docenti, setDocenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [editingSession, setEditingSession] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [exporting, setExporting] = useState(false);
  const videoInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    docente_id: "",
    date: "",
    time: "",
    duration_hours: 2,
    min_participants: 3,
    max_participants: 15,
    video_url: "",
    notes: "",
    zoom_link: "",
    zoom_password: "",
  });

  const durationOptions = [1, 2, 3, 4, 5, 6];

  useEffect(() => {
    fetchSessions();
    fetchDocenti();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/sessions`);
      setSessions(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento delle sessioni");
    } finally {
      setLoading(false);
    }
  };

  const fetchDocenti = async () => {
    try {
      const response = await axios.get(`${API}/docenti`);
      setDocenti(response.data);
    } catch (error) {
      console.error("Errore nel caricamento dei docenti:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleVideoUpload = async (file) => {
    if (!file) return;

    setUploadingVideo(true);
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      const fullUrl = `${window.location.origin}${response.data.url}`;
      setFormData({ ...formData, video_url: fullUrl });
      toast.success("Video caricato con successo");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante l'upload");
    } finally {
      setUploadingVideo(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      docente_id: "",
      date: "",
      time: "",
      duration_hours: 2,
      min_participants: 3,
      max_participants: 15,
      video_url: "",
      notes: "",
      zoom_link: "",
      zoom_password: "",
    });
    setEditingSession(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (session) => {
    setEditingSession(session);
    setFormData({
      title: session.title,
      description: session.description,
      docente_id: session.docente_id,
      date: session.date,
      time: session.time,
      duration_hours: session.duration_hours,
      min_participants: session.min_participants,
      max_participants: session.max_participants,
      video_url: session.video_url || "",
      notes: session.notes || "",
      zoom_link: session.zoom_link || "",
      zoom_password: session.zoom_password || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const sessionData = {
      ...formData,
      duration_hours: parseInt(formData.duration_hours),
      min_participants: parseInt(formData.min_participants),
      max_participants: parseInt(formData.max_participants),
    };

    try {
      if (editingSession) {
        await axios.put(`${API}/admin/sessions/${editingSession.session_id}`, sessionData);
        toast.success("Sessione aggiornata con successo");
      } else {
        await axios.post(`${API}/admin/sessions`, sessionData);
        toast.success("Sessione creata con successo");
      }
      setDialogOpen(false);
      resetForm();
      fetchSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!sessionToDelete) return;

    try {
      await axios.delete(`${API}/admin/sessions/${sessionToDelete.session_id}`);
      toast.success("Sessione eliminata");
      fetchSessions();
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    } finally {
      setDeleteDialogOpen(false);
      setSessionToDelete(null);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/sessions/export`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'sessioni.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Export completato!");
    } catch (error) {
      toast.error("Errore durante l'export");
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("it-IT", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-2">
              Gestione Sessioni
            </h1>
            <p className="text-muted-foreground">
              Crea e gestisci le sessioni formative dei docenti
            </p>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0">
            <Button
              variant="outline"
              onClick={handleExportCSV}
              className="rounded-full"
              disabled={exporting}
              data-testid="export-sessions-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? "Export..." : "Export CSV"}
            </Button>
            <Button
              onClick={openCreateDialog}
              className="rounded-full"
              data-testid="create-session-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuova Sessione
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-secondary rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : sessions.length > 0 ? (
          <div className="space-y-4">
            {sessions.map((session) => (
              <Card key={session.session_id} className="rounded-2xl border-border/50 overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row">
                    {/* Left: Date/Time Badge */}
                    <div className="bg-primary/10 p-6 flex flex-col items-center justify-center min-w-[140px]">
                      <span className="text-2xl font-bold text-primary">
                        {new Date(session.date).getDate()}
                      </span>
                      <span className="text-sm text-primary/80 uppercase">
                        {new Date(session.date).toLocaleDateString("it-IT", { month: "short" })}
                      </span>
                      <span className="mt-2 text-lg font-medium text-primary">
                        {session.time}
                      </span>
                    </div>
                    
                    {/* Middle: Session Info */}
                    <div className="flex-1 p-6">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="font-serif text-lg font-medium">{session.title}</h3>
                        <Badge variant="secondary" className="rounded-full">
                          {session.duration_hours}h
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {session.description}
                      </p>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {session.docente_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {session.current_participants}/{session.max_participants} iscritti
                          <span className="text-xs">(min: {session.min_participants})</span>
                        </span>
                        {session.video_url && (
                          <span className="flex items-center gap-1 text-green-600">
                            <Video className="w-4 h-4" />
                            Video
                          </span>
                        )}
                        {session.zoom_link && (
                          <a 
                            href={session.zoom_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-700 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Video className="w-4 h-4" />
                            Zoom
                          </a>
                        )}
                        {session.materials.length > 0 && (
                          <span className="flex items-center gap-1">
                            <FileText className="w-4 h-4" />
                            {session.materials.length} materiali
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Right: Actions */}
                    <div className="flex md:flex-col gap-2 p-4 md:p-6 border-t md:border-t-0 md:border-l border-border/50">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full flex-1 md:flex-none"
                        onClick={() => openEditDialog(session)}
                        data-testid={`edit-session-${session.session_id}`}
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Modifica
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => {
                          setSessionToDelete(session);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-session-${session.session_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="rounded-2xl border-border/50">
            <CardContent className="py-16 text-center">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground mb-4">Nessuna sessione programmata</p>
              <Button onClick={openCreateDialog} className="rounded-full">
                Crea la prima sessione
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {editingSession ? "Modifica Sessione" : "Nuova Sessione"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="title">Titolo *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="rounded-xl"
                  placeholder="es. Sessione Formativa Estetica Oncologica"
                  data-testid="session-title-input"
                />
              </div>

              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="description">Descrizione *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={3}
                  className="rounded-xl"
                  placeholder="Descrivi il contenuto della sessione..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="docente_id">Docente *</Label>
                <Select
                  value={formData.docente_id}
                  onValueChange={(v) => handleSelectChange("docente_id", v)}
                  required
                >
                  <SelectTrigger className="rounded-xl" data-testid="docente-select">
                    <SelectValue placeholder="Seleziona docente" />
                  </SelectTrigger>
                  <SelectContent>
                    {docenti.map((docente) => (
                      <SelectItem key={docente.docente_id} value={docente.docente_id}>
                        <div className="flex items-center gap-2">
                          {docente.photo_url && (
                            <img 
                              src={docente.photo_url} 
                              alt={docente.name}
                              className="w-6 h-6 rounded-full object-cover"
                            />
                          )}
                          {docente.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="duration_hours">Durata *</Label>
                <Select
                  value={formData.duration_hours.toString()}
                  onValueChange={(v) => handleSelectChange("duration_hours", parseInt(v))}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {durationOptions.map((h) => (
                      <SelectItem key={h} value={h.toString()}>
                        {h} {h === 1 ? "ora" : "ore"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="date">Data *</Label>
                <Input
                  id="date"
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="time">Ora *</Label>
                <Input
                  id="time"
                  name="time"
                  type="time"
                  value={formData.time}
                  onChange={handleChange}
                  required
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="min_participants">Min Partecipanti *</Label>
                <Input
                  id="min_participants"
                  name="min_participants"
                  type="number"
                  min="1"
                  value={formData.min_participants}
                  onChange={handleChange}
                  required
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="max_participants">Max Partecipanti *</Label>
                <Input
                  id="max_participants"
                  name="max_participants"
                  type="number"
                  min="1"
                  value={formData.max_participants}
                  onChange={handleChange}
                  required
                  className="rounded-xl"
                />
              </div>

              {/* Video YouTube/Vimeo Link */}
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="video_url">Link Video (YouTube, Vimeo)</Label>
                <Input
                  id="video_url"
                  name="video_url"
                  value={formData.video_url}
                  onChange={handleChange}
                  className="rounded-xl"
                  placeholder="https://www.youtube.com/watch?v=... oppure https://vimeo.com/..."
                />
                <p className="text-xs text-muted-foreground">
                  Incolla il link del video da YouTube o Vimeo. Il video sarà visibile alle clienti iscritte.
                </p>
                {formData.video_url && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-xl">
                    <p className="text-sm text-green-700 flex items-center gap-2">
                      <Video className="w-4 h-4" />
                      Video collegato: <a href={formData.video_url} target="_blank" rel="noopener noreferrer" className="underline">{formData.video_url.substring(0, 50)}...</a>
                    </p>
                  </div>
                )}
              </div>

              {/* Link Zoom */}
              <div className="md:col-span-2 space-y-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-center gap-2 text-blue-700">
                  <Video className="w-5 h-5" />
                  <span className="font-medium">Videoconferenza Zoom</span>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zoom_link">Link Zoom Meeting</Label>
                  <Input
                    id="zoom_link"
                    name="zoom_link"
                    value={formData.zoom_link}
                    onChange={handleChange}
                    className="rounded-xl"
                    placeholder="https://zoom.us/j/123456789"
                  />
                  <p className="text-xs text-muted-foreground">
                    Crea un meeting su Zoom e incolla qui il link di partecipazione
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zoom_password">Password Zoom (opzionale)</Label>
                  <Input
                    id="zoom_password"
                    name="zoom_password"
                    value={formData.zoom_password}
                    onChange={handleChange}
                    className="rounded-xl"
                    placeholder="abc123"
                  />
                </div>
              </div>

              {/* Note */}
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="notes">Note Aggiuntive</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={2}
                  className="rounded-xl"
                  placeholder="es. Portare abbigliamento comodo..."
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="rounded-full"
                onClick={() => setDialogOpen(false)}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                className="rounded-full"
                disabled={saving || !formData.docente_id}
                data-testid="save-session-btn"
              >
                {saving ? "Salvataggio..." : editingSession ? "Salva Modifiche" : "Crea Sessione"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina sessione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare la sessione "{sessionToDelete?.title}" del {sessionToDelete && formatDate(sessionToDelete.date)}?
              Questa azione eliminerà anche tutte le iscrizioni associate.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
};

export default AdminSessionsPage;
